import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { createSurchargesSchema } from '../../assets/schemas/schemas';
import { initialPager } from '../../components/views/Parameters/parametersConstants';
import { surchargesService } from '../../services/surchargesService';
import { notify } from '../../assets/helpers/toast';
import { useForm } from 'react-hook-form';
import { inactiveRowCount } from '../../components/views/Parameters/parametersTableUtils';
import { salesChannelsService } from '../../services/commercialService';

export default function useSurcharges() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
    control,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(createSurchargesSchema()),
    defaultValues: {
      contractTypes: [],
      dateFrom: '',
      dateTo: '',
      description: '',
      saleChannel: null,
      useRenewDiscount: false,
      useRenewSurcharge: false,
    },
  });

  const [showSurchargesForm, setShowSurchargesForm] = useState(false);

  const [surchargesData, setSurchargesData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });

  const [editingSurchargesId, setEditingSurchargesId] = useState(null);

  const handleSurchargesPageChange = useCallback((_e, page) => {
    setSurchargesData((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleToggleSSurchargeActive = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await surchargesService.updateSurcharge(row._id, {
      isActive: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Activo' : 'Desactivado', 'success');
      setSurchargesData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === row._id ? { ...item, isActive: next } : item
        ),
      }));
    } else {
      notify(
        response?.message ??
          response?.data?.message ??
          'Error al actualizar el estado del modelo',
        'error'
      );
    }
  }, []);

  const handleEditSSurcharge = useCallback(
    (row) => {
      setShowSurchargesForm(true);
      setEditingSurchargesId(row._id);
      console.log('Row data for editing:', row); // Agrega este log para verificar los datos de la fila
      reset({
        contractTypes: row.contractTypes,
        dateFrom: new Date(row.dateFrom),
        dateTo: new Date(row.dateTo),
        description: row.description,
        saleChannel: {
          _id: row.saleChannel?._id,
          name: row.saleChannel?.channel,
        },
        useRenewDiscount: row.useRenewDiscount,
        useRenewSurcharge: row.useRenewSurcharge,
      });
    },
    [reset]
  );

  const onSurchargesSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setSurchargesData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };

  const fetchSurcharges = useCallback(
    async (abortSignal) => {
      setSurchargesData((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await surchargesService.getSurcharges({
          page: surchargesData.page,
          limit: surchargesData.limit,
        });

        if (abortSignal?.aborted) return;

        if ([200, 201].includes(response?.status)) {
          const rows =
            response?.data?.data?.map((surcharge) => ({
              ...surcharge,
              })) || [];

          setSurchargesData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
              response?.data?.message ??
              'Error al cargar la lista de recargos',
            'error'
          );
          setSurchargesData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (abortSignal?.aborted) return;
        notify('Error al cargar la lista de recargos', 'error');
        setSurchargesData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [surchargesData.page, surchargesData.limit]
  );

  const onSubmit = handleSubmit(async (data) => {

    if (editingSurchargesId) {
      const response = await surchargesService.updateSurcharge(
        editingSurchargesId,
        { 
          ...data, 
          saleChannel: data.saleChannel?._id, 
          dateFrom: data.dateFrom ? new Date(data.dateFrom).toISOString() : '', 
          dateTo: data.dateTo ? new Date(data.dateTo).toISOString() : ''
        } // Asegúrate de enviar el ID del canal de venta y las fechas en formato ISO
      );

      if ([200, 201].includes(response?.status)) {
        notify('Recargo actualizado', 'success');
        fetchSurcharges();
        setEditingSurchargesId(null);
        setShowSurchargesForm(false);
        reset({
          contractTypes: '',
          dateFrom: '',
          dateTo: '',
          description: '',
          saleChannel: '',
          useRenewDiscount: false,
          useRenewSurcharge: false,
        });
      } else {
        notify(
          response?.message ??
            response?.data?.message ??
            'Error al actualizar el recargo',
          'error'
        );
      }
      return;
    }

    const response = await surchargesService.createSurcharge({
      ...data,
      saleChannel: data.saleChannel?._id,
      dateFrom: data.dateFrom ? new Date(data.dateFrom).toISOString() : '', 
          dateTo: data.dateTo ? new Date(data.dateTo).toISOString() : ''
    });

    if ([200, 201].includes(response?.status)) {
      notify('Recargo creado', 'success');
      reset({
        contractTypes: '',
        dateFrom: '',
        dateTo: '',
        description: '',
        saleChannel: '',
        useRenewDiscount: false,
        useRenewSurcharge: false,
      });
      setShowSurchargesForm(false);
      fetchSurcharges();
    } else {
      notify(
        response?.message ??
          response?.data?.message ??
          'Error al crear el recargo',
        'error'
      );
    }
  });

    const getSalesChannelsByName = useCallback(
      async (name) => {
        try {
          const response = await salesChannelsService.getSalesChannels({
            offset: 0,
            limit: 20,
            description: name
          });
  
          if ([200, 201].includes(response?.status)) {
            const rows =
              response?.data?.saleChannels?.map((channel) => ({
                _id: channel._id,
                name: channel.name,
                shortName: channel.shortName,
                value: channel._id,
                label: `(${channel.shortName}) - ${channel.name}`,  
              })) || [];
  
            return rows;
          } else {
            notify(
              response?.message ??
                response?.data?.message ??
                'Error al cargar la lista de canales de venta',
              'error'
            );
            return [];
          }
        } catch (error) {
          notify('Error al cargar la lista de canales de venta', 'error');
          return [];
        }
      },
      []
    );

    const closeForm = () => {
    setShowSurchargesForm(false);
    setEditingSurchargesId(null);
    reset({ contractTypes: '', dateFrom: '', dateTo: '', description: '', saleChannel: '', useRenewDiscount: false, useRenewSurcharge: false });
  }

    useEffect(() => {
      const abortController = new AbortController();
      fetchSurcharges(abortController.signal);
  
      return () => {
        abortController.abort();
      };
    }, [fetchSurcharges]);

  const inactiveSurcharges = inactiveRowCount(surchargesData.data);

  const clearErrorByFiueldName = (fieldName) => {
    setError(fieldName, null);
    clearErrors(fieldName);
  }

  return {
    register,
    handleSubmit,
    reset,
    errors,
    showSurchargesForm,
    setShowSurchargesForm,
    surchargesData,
    setSurchargesData,
    editingSurchargesId,
    setEditingSurchargesId,
    handleSurchargesPageChange,
    handleToggleSSurchargeActive,
    handleEditSSurcharge,
    onSubmit,
    onSurchargesSelectPerPage,
    inactiveSurcharges,
    closeForm,
    getSalesChannelsByName,
    setValue,
    getValues,
    control,
    clearErrorByFiueldName
  };
}
